import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Calendar } from "lucide-react";
import { usePlatform } from "@/components/platform/PlatformProvider";
import { useAppTranslation } from "@/i18n/useAppTranslation";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  url: string;
  featureImage: string | null;
  publishedAt: string | null;
  authorName: string | null;
  authorImage: string | null;
}

interface BlogResponse {
  posts: BlogPost[];
}

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

async function fetchDashboardBlogPosts(): Promise<BlogResponse> {
  const res = await fetch(`${API_BASE}/company/blog-posts?limit=3`);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to load posts (${res.status})`);
  }
  return (await res.json()) as BlogResponse;
}

export function DashboardBlogFeed() {
  const { t } = useAppTranslation();
  const config = usePlatform();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["dashboard", "blog-posts"],
    queryFn: fetchDashboardBlogPosts,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  return (
    <section data-testid="dashboard-blog-feed">
      <div className="flex items-center justify-between">
        <h2 className="font-sans text-xl font-bold text-brand">{t("dashboard.blogFeed.title", "From our blog")}</h2>
        <a
          href={config.blogUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
        >
          {t("dashboard.blogFeed.read_more", "Read more on our blog")} <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="overflow-hidden rounded-2xl border border-border bg-background">
                <div className="aspect-[16/10] w-full animate-pulse bg-muted" />
                <div className="p-4">
                  <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
                  <div className="mt-3 h-3 w-full animate-pulse rounded bg-muted" />
                  <div className="mt-2 h-3 w-5/6 animate-pulse rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="rounded-2xl border border-border bg-background p-6 text-center shadow-sm" data-testid="dashboard-blog-error">
            <p className="text-sm text-[#5a6b5a]">
              {t("dashboard.blogFeed.error", "We couldn't load the latest blog posts right now.")}
            </p>
            <a
              href={config.blogUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
            >
              {t("dashboard.blogFeed.visit", "Visit our blog")} <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        ) : data && data.posts.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.posts.map((post) => (
              <a
                key={post.id}
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition hover:shadow-md"
                data-testid={`dashboard-blog-card-${post.id}`}
              >
                <div className="aspect-[16/10] w-full overflow-hidden bg-muted">
                  {post.featureImage ? (
                    <img
                      src={post.featureImage}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-[#f0f5f0] text-brand">
                      <Calendar className="h-8 w-8" />
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="text-sm font-bold leading-snug text-brand group-hover:underline">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-[#5a6b5a]">
                      {post.excerpt}
                    </p>
                  )}
                  {post.publishedAt && (
                    <time
                      dateTime={post.publishedAt}
                      className="mt-auto pt-3 text-[11px] text-[#5a6b5a]/70"
                    >
                      {new Date(post.publishedAt).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </time>
                  )}
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-background p-6 text-center shadow-sm" data-testid="dashboard-blog-empty">
            <p className="text-sm text-[#5a6b5a]">{t("dashboard.blogFeed.empty", "No blog posts available right now.")}</p>
            <a
              href={config.blogUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
            >
              {t("dashboard.blogFeed.visit", "Visit our blog")} <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
